/**
 * El dock de chats: ventanas de WhatsApp acopladas abajo, como Messenger o como las ventanas de
 * redacción de Gmail.
 *
 * POR QUÉ NO UN MODAL. Un modal es exclusivo: tapa la pantalla de atrás y obliga a cerrarlo antes
 * de hablar con el siguiente cliente. El operador que despacha treinta pedidos abre y cierra
 * treinta veces, y —peor— mientras escribe NO ve el pedido del que está hablando. El dock resuelve
 * las dos cosas: el trabajo de atrás queda a la vista, y se pueden dejar dos o tres conversaciones
 * esperando respuesta en vez de una sola bloqueante.
 *
 * POR QUÉ VIVE EN EL SHELL Y NO EN UNA PANTALLA. Si el dock fuera de Operaciones, al navegar a CRM
 * las ventanas morirían con el borrador adentro, y CRM tendría que construir el suyo. Acá arriba,
 * cualquier pantalla llama `useWaChatDock().abrir({...})` y hereda el mismo chat. Mismo criterio
 * que [WaInboxNavContext], que existe para sobrevivir a los cambios de ruta.
 *
 * 🪤 MINIMIZAR NO PUEDE DESMONTAR, Y AHÍ ESTÁ TODO EL VALOR. Si al minimizar se desmonta la
 * ventana, se pierde el borrador a medio escribir y la posición del scroll — que es exactamente lo
 * que uno guarda cuando minimiza. El dock por eso mantiene montadas TODAS las ventanas abiertas y
 * solo esconde las minimizadas con CSS. Este archivo guarda la lista; el que la respeta es
 * [WaChatDock].
 *
 * 🪤 Y POR ESO HAY UN TOPE DE TRES. Cada ventana montada es una suscripción de realtime y una lista
 * de mensajes viva. Sin tope, una tarde de trabajo deja veinte suscripciones abiertas. Al abrir la
 * cuarta, la más vieja se minimiza —no se cierra, que perdería su borrador— y queda como píldora.
 */

import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { RastroDePruebaProvider } from './RastroDePruebaContext';
import type { VariablesDePlantilla } from '@/lib/whatsapp/plantillasDeMensaje';
import type { ProductoDeLaPrueba } from '@/lib/products/elProductoDeLaPrueba';

/** Cuántas ventanas pueden estar EXPANDIDAS a la vez. Las demás quedan como píldora. */
export const MAX_VENTANAS_ABIERTAS = 3;

/**
 * Altura del dock en la pila de capas. Por encima de la tarjeta de llamada activa (10150) y por
 * debajo del copiloto (99990).
 *
 * 🪤 VIVE ACÁ, NO EN EL COMPONENTE, PORQUE NO ES UN NÚMERO SUELTO: ES UNA RELACIÓN. El menú de
 * chats del encabezado tiene que quedar POR ENCIMA de las ventanas —si no, la lista se abre detrás
 * de los chats y no se puede elegir—, y deriva su capa de esta. Con el número escrito a mano en dos
 * archivos, mover uno rompe el otro en silencio.
 */
export const Z_DOCK = 10200;

/**
 * Qué hay dentro de una ventana acoplada.
 *
 * 🚨 LA PRUEBA DEL AGENTE ES UNA VENTANA MÁS, NO UN DOCK APARTE. Antes vivía en su propio portal en la
 * misma esquina, así que se dibujaba ENCIMA de los chats reales en vez de al lado. El founder lo pidió
 * así: «debe comportarse como un chat más… al costado y cerrarse tipo burbuja como en Messenger».
 *
 * Y hacerlo bien no es moverla de lugar: es que la administre el MISMO dock. Así hereda gratis el
 * acomodo lateral, el tope de tres abiertas, la minimización en píldora y el no perder el borrador —
 * y no hay dos sistemas de ventanas que mantener sincronizados.
 */
export type TipoDeVentana = 'cliente' | 'prueba';

export interface ChatAcoplado {
  /** Identidad de la ventana. Para un cliente es su teléfono; para una prueba, el id del agente. */
  id: string;
  tipo: TipoDeVentana;
  telefono: string;
  nombre: string;
  /** Solo en las de prueba: por qué canal se está probando (cambia el formato de la respuesta). */
  canal?: string;
  /** Solo en las de prueba: el agente que se está probando. */
  agenteId?: string;
  /**
   * Solo en las de prueba: el BORRADOR de la spec que se está editando en el constructor.
   *
   * 🚨 PROBAR EL BORRADOR ES EL PUNTO. Sin esto, el sandbox habla con lo que ya está publicado y
   * el ciclo queda al revés: habría que publicar a producción —con el examen, que cuesta plata—
   * para recién ahí ver si el cambio sirve. Viaja como dato opaco hasta el endpoint, que decide
   * la precedencia (borrador → publicada → clásico).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- la spec la tipa el constructor
  spec?: any;
  /**
   * Solo en las de prueba: qué recorrido se está probando.
   *
   * 🚨 EL PREVIEW PRUEBA EL FLUJO ABIERTO. Sin esto, abrir «Posventa» y tocar Preview arrancaba una
   * conversación de VENTA: en el sandbox el recorrido lo elige el detector de intención, y las
   * señales que empujarían a posventa —pedido en curso, ya compró— van en falso a propósito
   * porque no hay cliente. Se probaba siempre el mismo embudo con el flujo equivocado al lado.
   */
  recorrido?: string;
  /** Solo en las de prueba: el producto con el que se prueba «Venta por producto» (15-sep). */
  producto?: ProductoDeLaPrueba | null;
  /** Solo en las de prueba: el cuaderno de la venta a la derecha del chat (15-sep). Abierto por defecto. */
  cuaderno?: boolean;
  /**
   * Cuántas veces se reinició esta prueba.
   *
   * 🪤 REINICIAR ES REMONTAR, no vaciar estado a mano. El contador viaja al `key` del componente, así
   * que React lo desmonta y lo vuelve a montar limpio: la conversación, el borrador y el error se van
   * juntos. Vaciarlos uno por uno es la forma de olvidarse de uno.
   */
  reinicios?: number;
  /**
   * Contexto de por qué se abrió: "#1042", "Lead", el nombre de la tienda. Se muestra junto al
   * nombre porque quien despacha identifica al cliente por su pedido, no solo por su nombre.
   */
  etiqueta?: string;
  /**
   * Qué se sabe del cliente para rellenar las plantillas del compositor: el producto que compró,
   * hace cuánto, el número de pedido.
   *
   * ⭐ VIAJA CON LA VENTANA PORQUE PERTENECE A LA VENTANA. «Por recomprar» sabe que a esta persona
   * se le acabó el DuroMax hace tres meses; el compositor, solo, no tiene cómo enterarse. Guardado
   * acá sobrevive a minimizar y a cambiar de pantalla, igual que el borrador.
   */
  contexto?: VariablesDePlantilla;
  minimizado: boolean;
}

interface DockApi {
  chats: ChatAcoplado[];
  abrir: (chat: { telefono: string; nombre: string; etiqueta?: string; contexto?: VariablesDePlantilla }) => void;
  /** Abre (o trae al frente) la ventana de prueba de un agente. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- la spec la tipa el constructor
  abrirPrueba: (p: { agenteId: string; nombre: string; canal: string; spec?: any; recorrido?: string; producto?: ProductoDeLaPrueba | null }) => void;
  /**
   * Poner al día lo que la ventana de prueba está probando, SIN reabrirla ni desminimizarla.
   *
   * 🚨 LA VENTANA TOMABA UNA FOTO DE LA SPEC AL ABRIRSE. Editabas un paso con la prueba abierta y
   * el sandbox seguía contestando con el flujo viejo: la conversación decía una cosa y el lienzo
   * mostraba otra, que es exactamente el engaño que la prueba tiene que evitar.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- la spec la tipa el constructor
  refrescarPrueba: (agenteId: string, p: { spec?: any; recorrido?: string; producto?: ProductoDeLaPrueba | null }) => void;
  /** Abre o cierra el cuaderno de la venta de una ventana de prueba. */
  alternarCuaderno: (id: string) => void;
  /** Vacía la conversación de prueba sin cerrar la ventana. */
  reiniciarPrueba: (id: string) => void;
  cerrar: (id: string) => void;
  alternarMinimizado: (id: string) => void;
}

const Ctx = createContext<DockApi | null>(null);

/** Los últimos 9 dígitos son la identidad real del cliente en Perú, sin prefijo ni espacios. */
function idDeTelefono(telefono: string): string {
  return telefono.replace(/\D/g, '').slice(-9);
}

export function WaChatDockProvider({ children }: { children: ReactNode }) {
  const [chats, setChats] = useState<ChatAcoplado[]>([]);

  const abrir = useCallback((chat: { telefono: string; nombre: string; etiqueta?: string; contexto?: VariablesDePlantilla }) => {
    const id = idDeTelefono(chat.telefono);
    if (!id) return;

    setChats((prev) => {
      const yaEsta = prev.find((c) => c.id === id);
      /*
       * Reabrir un chat que ya está: se expande y se trae al frente, no se duplica.
       *
       * 🪤 EL CONTEXTO SE REFRESCA, igual que la etiqueta. Si el operador vuelve a abrir el mismo
       *    cliente desde otra fila —otro producto vencido—, las plantillas tienen que hablar del
       *    producto NUEVO. Conservar el viejo haría que el mensaje mencione lo que no era.
       */
      if (yaEsta) {
        return [...prev.filter((c) => c.id !== id), {
          ...yaEsta,
          minimizado: false,
          etiqueta: chat.etiqueta ?? yaEsta.etiqueta,
          contexto: chat.contexto ?? yaEsta.contexto,
        }];
      }

      const nuevo: ChatAcoplado = { id, tipo: 'cliente', telefono: chat.telefono, nombre: chat.nombre, etiqueta: chat.etiqueta, contexto: chat.contexto, minimizado: false };
      const conElNuevo = [...prev, nuevo];

      // Tope de expandidas: la más vieja se MINIMIZA (conserva su borrador), no se cierra.
      const expandidas = conElNuevo.filter((c) => !c.minimizado);
      if (expandidas.length > MAX_VENTANAS_ABIERTAS) {
        const aMinimizar = expandidas[0].id;
        return conElNuevo.map((c) => (c.id === aMinimizar ? { ...c, minimizado: true } : c));
      }
      return conElNuevo;
    });
  }, []);

  /**
   * Abre la ventana de prueba del agente. Misma mecánica que un chat: si ya está, se expande y se
   * trae al frente en vez de duplicarse.
   *
   * 🪤 EL ID LLEVA PREFIJO `prueba:` PARA QUE NO PUEDA CHOCAR con el de un cliente. Sin eso, un agente
   * cuyo id se pareciera a un teléfono normalizado reabriría el chat de una persona real.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- la spec la tipa el constructor
  const abrirPrueba = useCallback((p: { agenteId: string; nombre: string; canal: string; spec?: any; recorrido?: string; producto?: ProductoDeLaPrueba | null }) => {
    const id = `prueba:${p.agenteId}`;
    setChats((prev) => {
      const yaEsta = prev.find((c) => c.id === id);
      if (yaEsta) {
        // 🪤 LA SPEC SE REFRESCA AL REABRIR, no solo al crear la ventana: editás una etapa, volvés
        // a tocar «Probar agente» y tenés que estar probando lo NUEVO. Con la ventana ya abierta,
        // conservar el borrador viejo sería el mismo engaño que esto vino a arreglar.
        // 🪤 El recorrido se refresca igual que la spec: si cambiaste de flujo y volviste a
        // tocar Preview, tenés que estar probando el flujo NUEVO, no el de la ventana vieja.
        return [...prev.filter((c) => c.id !== id), {
          ...yaEsta, minimizado: false, canal: p.canal, spec: p.spec, recorrido: p.recorrido, producto: p.producto,
        }];
      }
      const nuevo: ChatAcoplado = {
        id, tipo: 'prueba', telefono: '', nombre: p.nombre,
        canal: p.canal, agenteId: p.agenteId, spec: p.spec, recorrido: p.recorrido, producto: p.producto, cuaderno: true, minimizado: false,
      };
      const conElNuevo = [...prev, nuevo];
      const expandidas = conElNuevo.filter((c) => !c.minimizado);
      if (expandidas.length > MAX_VENTANAS_ABIERTAS) {
        const aMinimizar = expandidas[0].id;
        return conElNuevo.map((c) => (c.id === aMinimizar ? { ...c, minimizado: true } : c));
      }
      return conElNuevo;
    });
  }, []);

  const reiniciarPrueba = useCallback((id: string) => {
    setChats((prev) => prev.map((c) => (c.id === id ? { ...c, reinicios: (c.reinicios ?? 0) + 1 } : c)));
  }, []);

  const cerrar = useCallback((id: string) => {
    setChats((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const alternarMinimizado = useCallback((id: string) => {
    setChats((prev) => {
      const objetivo = prev.find((c) => c.id === id);
      if (!objetivo) return prev;

      // Expandir una píldora también cuenta contra el tope.
      if (objetivo.minimizado) {
        const expandidas = prev.filter((c) => !c.minimizado);
        const desplazado = expandidas.length >= MAX_VENTANAS_ABIERTAS ? expandidas[0].id : null;
        return prev.map((c) => {
          if (c.id === id) return { ...c, minimizado: false };
          if (c.id === desplazado) return { ...c, minimizado: true };
          return c;
        });
      }
      return prev.map((c) => (c.id === id ? { ...c, minimizado: true } : c));
    });
  }, []);

  /*
   * 🪤 SI LA VENTANA NO ESTÁ ABIERTA, NO HACE NADA — y no la abre. Abrirla sola al editar sería una
   * ventana que aparece encima del lienzo cada vez que alguien escribe una letra.
   *
   * 🪤 Y SI NADA CAMBIÓ, DEVUELVE EL MISMO ARREGLO: sin esto, cada tecla del constructor volvería a
   * dibujar el dock entero y las ventanas de chat reales que estén al lado.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- la spec la tipa el constructor
  const alternarCuaderno = useCallback((id: string) => {
    setChats((prev) => prev.map((c) => (c.id === id ? { ...c, cuaderno: !(c.cuaderno ?? true) } : c)));
  }, []);
  const refrescarPrueba = useCallback((agenteId: string, p: { spec?: any; recorrido?: string; producto?: ProductoDeLaPrueba | null }) => {
    const id = `prueba:${agenteId}`;
    setChats((prev) => {
      const actual = prev.find((c) => c.id === id);
      if (!actual) return prev;
      if (actual.spec === p.spec && actual.recorrido === p.recorrido && actual.producto === p.producto) return prev;
      return prev.map((c) => (c.id === id ? { ...c, spec: p.spec, recorrido: p.recorrido, producto: p.producto } : c));
    });
  }, []);

  const api = useMemo(
    () => ({ chats, abrir, abrirPrueba, refrescarPrueba, reiniciarPrueba, alternarCuaderno, cerrar, alternarMinimizado }),
    [chats, abrir, abrirPrueba, refrescarPrueba, reiniciarPrueba, alternarCuaderno, cerrar, alternarMinimizado],
  );
  /*
   * El rastro de la prueba cuelga de acá para no tocar el árbol de [AppLayout], pero es un contexto
   * APARTE a propósito: cambia en cada turno del agente y no tiene por qué volver a renderizar las
   * ventanas de chat de clientes reales. Ver [RastroDePruebaContext].
   */
  return (
    <Ctx.Provider value={api}>
      <RastroDePruebaProvider>{children}</RastroDePruebaProvider>
    </Ctx.Provider>
  );
}

/**
 * 🪤 DEVUELVE UN API INERTE SI NO HAY PROVIDER, EN VEZ DE REVENTAR. El dock cuelga del shell del
 * admin, y hay pantallas que viven fuera de él (el editor de temas, el login). Que una lista se
 * caiga entera porque el botón de WhatsApp no encontró su contexto sería un intercambio pésimo:
 * ahí el botón simplemente no hace nada.
 */
export function useWaChatDock(): DockApi {
  return useContext(Ctx) ?? {
    chats: [], abrir: () => {}, abrirPrueba: () => {}, refrescarPrueba: () => {}, alternarCuaderno: () => {},
    reiniciarPrueba: () => {}, cerrar: () => {}, alternarMinimizado: () => {},
  };
}
